import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getMyPhoneNumber } from "@/services/phone-numbers.service";
import type { TMyCallerId, TMyPhoneNumber } from "@/models/phone-number.model";

type CallerIdState = {
  /** Number outbound calls/texts leave from (the agent's, or the company line). */
  callerId: TMyCallerId | null;
  /** True once a /phone-number/me response has been seen this session. */
  loaded: boolean;
  /** "Set up a phone number first" dialog (OutboundNumberPrompt, mounted once in the dashboard layout). */
  promptOpen: boolean;
};

type CallerIdActions = {
  /** Feed a /phone-number/me response fetched elsewhere (inbound-voice sync). */
  setMine: (mine: TMyPhoneNumber) => void;
  closePrompt: () => void;
  /**
   * Pre-flight gate for outbound calls and texts: is there a caller ID to
   * dial/text from? A cached number is trusted; "none" is re-checked against
   * the server so an agent who just got a number assigned isn't blocked until
   * re-login. When the check itself fails with nothing known, the attempt is
   * let through — the backend enforces NO_CALLER_ID and the send/dial surfaces
   * the real error.
   */
  ensureOutboundNumber: () => Promise<boolean>;
  /** The gate surfaces use: ensureOutboundNumber + the prompt when there's no number. */
  requireOutboundNumber: () => Promise<boolean>;
};

export const useCallerIdStore = create<CallerIdState & CallerIdActions>()(
  devtools(
    (set, get) => ({
      callerId: null,
      loaded: false,
      promptOpen: false,

      setMine: (mine) => set({ callerId: mine.callerId ?? null, loaded: true }, false, "setMine"),

      closePrompt: () => set({ promptOpen: false }, false, "closePrompt"),

      ensureOutboundNumber: async () => {
        if (get().callerId) return true;
        try {
          const mine = await getMyPhoneNumber();
          set({ callerId: mine.callerId ?? null, loaded: true }, false, "ensureOutboundNumber");
          return !!mine.callerId;
        } catch {
          return !get().loaded;
        }
      },

      requireOutboundNumber: async () => {
        const ok = await get().ensureOutboundNumber();
        if (!ok) set({ promptOpen: true }, false, "requireOutboundNumber/prompt");
        return ok;
      },
    }),
    { name: "caller-id-store" }
  )
);

/** The gate, callable outside React (context callbacks, plain handlers). */
export const requireOutboundNumber = () => useCallerIdStore.getState().requireOutboundNumber();
